<template>
  <div class="gift-entry-page">
    <div class="container">
      <div class="gift-icon-wrapper">
        <i class="fa-solid fa-gift gift-icon"></i>
      </div>
      <h1 class="title">Regala un Ingresso Gratuito</h1>
      <p class="subtitle">Puoi regalare 1 ingresso gratuito al mese. L'ingresso sarà valido per 1 anno.</p>

      <div class="card">
        <!-- Checking Status -->
        <div v-if="isChecking" class="alert alert-info">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <div class="alert-content">
            <span>Verifica disponibilità...</span>
          </div>
        </div>

        <!-- Check Result - Cannot Gift -->
        <div v-if="checkResult && !checkResult.canGift && !isChecking" class="alert alert-info">
          <i class="fa-solid fa-info-circle"></i>
          <div class="alert-content">
            <div class="fw-bold">{{ checkResult.message }}</div>
            <div v-if="checkResult.message && checkResult.message.includes('già regalato')" class="mt-2 text-sm">Potrai regalare un nuovo ingresso il prossimo mese.</div>
          </div>
        </div>

        <!-- Success Message -->
        <div v-if="sendResult && sendResult.success" class="alert alert-success">
          <i class="fa-solid fa-circle-check"></i>
          <div class="alert-content">
            <div class="fw-bold">Regalo inviato con successo!</div>
            <div class="mt-2">{{ sendResult.message }}</div>
            <div v-if="sendResult.status === 'COMPLETED'" class="mt-2 text-sm">
              L'ingresso è stato caricato immediatamente per l'utente registrato.
            </div>
            <div v-if="sendResult.status === 'PENDING'" class="mt-2 text-sm">
              È stata inviata un'email al destinatario con le istruzioni per registrarsi e ricevere l'ingresso.
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error && (!sendResult || !sendResult.success)" class="alert alert-danger">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <div>{{ error }}</div>
        </div>

        <!-- Form - Only show if can gift -->
        <div v-if="checkResult && checkResult.canGift && !isChecking">
          <div class="form-group">
            <label class="form-label">
              <i class="fa-solid fa-envelope me-2"></i>
              Email del destinatario
            </label>
            <input
              type="email"
              class="form-control"
              v-model="recipientEmail"
              :disabled="isSending"
              placeholder="esempio@email.com"
              @keypress.enter="onSendGift"
            />
            <div class="form-text">
              Se il destinatario è già registrato, riceverà l'ingresso immediatamente. 
              Se non è registrato, riceverà un'email con le istruzioni per registrarsi.
            </div>
          </div>

          <button
            class="btn w-100 mt-3"
            :class="recipientEmail.trim() && !isSending ? 'btn-custom' : 'btn-disabled'"
            :disabled="!recipientEmail.trim() || isSending"
            @click="onSendGift"
          >
            <span v-if="isSending">
              <i class="fa-solid fa-spinner fa-spin me-1"></i>Invio in corso...
            </span>
            <span v-else>
              <i class="fa-solid fa-gift me-1"></i>Invia Regalo
            </span>
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { API_CONFIG } from '@/config/api';

export default {
  name: 'GiftEntry',
  data() {
    return {
      userToken: null,
      recipientEmail: '',
      isChecking: false,
      isSending: false,
      checkResult: null,
      sendResult: null,
      error: null,
    };
  },
  mounted() {
    // Preleva il token dall'URL
    this.userToken = this.$route.query?.userToken;
    
    if (!this.userToken) {
      // Reindirizza al login con returnUrl per tornare qui dopo il login
      const returnUrl = encodeURIComponent('/gift-entry');
      this.$router.replace({ path: '/login', query: { returnUrl } });
      return;
    }

    // Verifica disponibilità al mount
    this.checkCanGift();
  },
  methods: {
    async checkCanGift() {
      if (!this.userToken) return;

      this.isChecking = true;
      this.error = null;
      this.checkResult = null;

      try {
        // Costruisci l'URL assoluto senza query params
        const apiUrl = `${API_CONFIG.BASE_URL}/api/gift-entry/check`;
        const res = await fetch(
          apiUrl,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.userToken}`,
            },
          }
        );

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          const errorMessage = errorData.message || 'Errore durante la verifica della disponibilità';
          
          // Se il token non è valido (401/403), reindirizza al login
          if (res.status === 401 || res.status === 403 || errorMessage.toLowerCase().includes('token') || errorMessage.toLowerCase().includes('non valido')) {
            const returnUrl = encodeURIComponent('/gift-entry');
            this.$router.replace({ path: '/login', query: { returnUrl } });
            return;
          }
          
          throw new Error(errorMessage);
        }

        const data = await res.json();
        this.checkResult = data;
        if (!data.success) {
          // Se il token non è valido, reindirizza al login
          if (data.message && (data.message.toLowerCase().includes('token') || data.message.toLowerCase().includes('non valido') || data.message.toLowerCase().includes('scaduto'))) {
            const returnUrl = encodeURIComponent('/gift-entry');
            this.$router.replace({ path: '/login', query: { returnUrl } });
            return;
          }
          this.error = data.message;
        }
      } catch (e) {
        const errorMessage = e?.message || 'Errore durante la verifica della disponibilità';
        
        // Se il token non è valido, reindirizza al login
        if (errorMessage.toLowerCase().includes('token') || errorMessage.toLowerCase().includes('non valido') || errorMessage.toLowerCase().includes('scaduto')) {
          const returnUrl = encodeURIComponent('/gift-entry');
          this.$router.replace({ path: '/login', query: { returnUrl } });
          return;
        }
        
        this.error = errorMessage;
        this.checkResult = {
          success: false,
          canGift: false,
          message: errorMessage,
        };
      } finally {
        this.isChecking = false;
      }
    },
    async onSendGift() {
      if (!this.userToken) {
        this.error = 'Token utente mancante';
        return;
      }

      if (!this.recipientEmail.trim()) {
        this.error = 'Inserisci l\'email del destinatario';
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.recipientEmail.trim())) {
        this.error = 'Inserisci un indirizzo email valido';
        return;
      }

      this.isSending = true;
      this.error = null;
      this.sendResult = null;

      try {
        // Costruisci l'URL assoluto senza query params
        const apiUrl = `${API_CONFIG.BASE_URL}/api/gift-entry/send`;
        const res = await fetch(
          apiUrl,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.userToken}`,
            },
            body: JSON.stringify({
              recipientEmail: this.recipientEmail.trim(),
            }),
          }
        );

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          const errorMessage = errorData.message || 'Errore durante l\'invio del regalo';
          
          // Se il token non è valido, reindirizza al login
          if (res.status === 401 || res.status === 403 || errorMessage.toLowerCase().includes('token') || errorMessage.toLowerCase().includes('non valido')) {
            const returnUrl = encodeURIComponent('/gift-entry');
            this.$router.replace({ path: '/login', query: { returnUrl } });
            return;
          }
          
          throw new Error(errorMessage);
        }

        const data = await res.json();
        this.sendResult = data;
        if (data.success) {
          this.recipientEmail = '';
          // Re-check availability after sending
          setTimeout(() => {
            this.checkCanGift();
          }, 1000);
        } else {
          // Se il token non è valido, reindirizza al login
          if (data.message && (data.message.toLowerCase().includes('token') || data.message.toLowerCase().includes('non valido'))) {
            const returnUrl = encodeURIComponent('/gift-entry');
            this.$router.replace({ path: '/login', query: { returnUrl } });
            return;
          }
          this.error = data.message;
        }
      } catch (e) {
        // Se il token non è valido, reindirizza al login
        const errorMessage = e?.message || 'Errore durante l\'invio del regalo';
        if (errorMessage.toLowerCase().includes('token') || errorMessage.toLowerCase().includes('non valido')) {
          const returnUrl = encodeURIComponent('/gift-entry');
          this.$router.replace({ path: '/login', query: { returnUrl } });
          return;
        }
        
        this.error = errorMessage;
        this.sendResult = {
          success: false,
          message: errorMessage,
        };
      } finally {
        this.isSending = false;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

.gift-entry-page {
  font-family: 'Montserrat', sans-serif;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #292D7D;
  color: white;
  padding: 2rem;
}

.container {
  max-width: 600px;
  width: 100%;
}

.gift-icon-wrapper {
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.gift-icon {
  font-size: 4rem;
  color: #e83539;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.title {
  font-family: 'Montserrat', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
}

.subtitle {
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 2rem;
}

.card {
  background-color: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 0;
  padding: 2rem;
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
  text-align: left;
}

.form-label {
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  color: white;
  display: block;
  margin-bottom: 0.5rem;
}

.form-control {
  font-family: 'Montserrat', sans-serif;
  background-color: rgba(255, 255, 255, 0);
  border: 2px solid white;
  border-radius: 0;
  color: white;
  padding: 10px 15px;
  width: 100%;
}

.form-control::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.form-control:focus {
  background-color: rgba(255, 255, 255, 0.05);
  border-color: white;
  color: white;
  outline: none;
  box-shadow: none;
}

.form-control:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-text {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
}

.btn {
  font-family: 'Montserrat', sans-serif;
  border-radius: 0;
  text-transform: uppercase;
  font-weight: 600;
  padding: 10px 20px;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.btn-custom {
  background-color: #e83539;
  color: white;
  font-size: 1.2rem;
}

.btn-custom:hover:not(:disabled) {
  background-color: #cc2e31;
  color: white;
}

.btn-custom:disabled {
  background-color: #999;
  cursor: not-allowed;
}

.btn-disabled {
  background-color: #666;
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.alert {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 0;
  border: 2px solid;
}

.alert-success {
  background-color: rgba(40, 167, 69, 0.2);
  border-color: #28a745;
  color: white;
}

.alert-danger {
  background-color: rgba(232, 53, 57, 0.2);
  border-color: #e83539;
  color: white;
}

.alert-info {
  background-color: rgba(23, 162, 184, 0.2);
  border-color: #17a2b8;
  color: white;
}

.alert-content {
  flex: 1;
  text-align: left;
}

.alert i {
  font-size: 1.5rem;
  margin-top: 0.2rem;
}

.w-100 {
  width: 100%;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.fw-bold {
  font-weight: 700;
}

.text-sm {
  font-size: 0.875rem;
}

.text-left {
  text-align: left;
}
</style>
